import React, { useMemo } from 'react';
import {
  DateRenderer,
  DateTimeRenderer,
  HistoryGrid,
  HistoryModal,
  createNumberFormatter,
} from '@1823-partners/core';

import { fetchDealTeamHistory } from '../api';
// The deal key and the intake message ids identify a row's provenance rather
// than the deal state a reviewer compares across versions, and the VARIANT
// payloads are kilobyte JSON blobs. All start hidden here; the grid's column
// menu still brings them back.
import {
  DEFAULT_HIDDEN_COLUMNS,
  isDateTimeType,
  isDateType,
  isNumericType,
  isStructuredType,
  unwrapStringEncodedJson,
} from '../utils/dealTeamFields';

// Marks a payload that Snowflake stored as VARIANT text rather than as an
// OBJECT — see the save-path round-trip that rewrites CONFIDENCE/RAW_EXTRACT.
const TEXT_ENCODED_PREFIX = 'text: ';

// Drop the whitespace that sits between JSON tokens without reformatting the
// tokens themselves. Parsing and re-stringifying would push numeric lexemes
// through a double, so an id past 2^53 in an extract would render rounded.
const minifyJsonText = (text) => {
  let minified = '';
  let inString = false;
  let escaped = false;
  for (const character of text) {
    if (inString) {
      minified += character;
      if (escaped) escaped = false;
      else if (character === '\\') escaped = true;
      else if (character === '"') inString = false;
      continue;
    }
    if (character === '"') {
      inString = true;
      minified += character;
      continue;
    }
    if (/\s/.test(character)) continue;
    minified += character;
  }
  return minified;
};

// Escape control characters so a scalar payload occupies one grid row without
// altering the characters it actually contains.
const escapeToSingleLine = (text) => JSON.stringify(text).slice(1, -1);

const isJsonText = (text) => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

// Snowflake returns VARIANT columns as indented JSON text, and a value that has
// been through a save round-trip can arrive double-encoded as a JSON string, so
// unwrap it and render one line instead of a wall of characters.
const compactJson = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'object') return JSON.stringify(value);
  const { text, wasStringEncoded } = unwrapStringEncodedJson(value);
  const trimmed = text.trim();
  // Only an object/array payload is minified, and only after it parses: any
  // other value keeps its own spacing, since collapsing runs would render the
  // distinct audit values 'A  B' and 'A B' identically.
  const compacted = /^[[{]/.test(trimmed) && isJsonText(trimmed)
    ? minifyJsonText(trimmed)
    : escapeToSingleLine(text);
  // A VARIANT holding the payload as text is a save-path defect, not an
  // equivalent value, so it stays distinguishable from a stored OBJECT instead
  // of rendering identically.
  return wasStringEncoded ? `${TEXT_ENCODED_PREFIX}${compacted}` : compacted;
};

const DealTeamHistoryModal = ({ open, onClose, metadata, row }) => {
  const keyColumn = metadata?.key_column;
  const keyValue = keyColumn ? row?.[keyColumn] : null;
  const title = row?.DEAL_NAME || row?.PROPERTY_NAME || keyValue || '';

  const columns = useMemo(() => (metadata?.columns || [])
    .filter((column) => (
      column.name !== metadata?.recency_column && column.name !== 'MODIFIED_BY'
    ))
    .map((column) => {
      const definition = {
        field: column.name,
        headerName: column.name.replaceAll('_', ' '),
        minWidth: 130,
        initialHide: DEFAULT_HIDDEN_COLUMNS.has(column.name) ||
          isStructuredType(column.data_type),
      };
      if (column.name === keyColumn || column.name === 'DEAL_NAME') {
        definition.pinned = 'left';
      }
      if (isNumericType(column.data_type)) {
        definition.filter = 'agNumberColumnFilter';
        definition.valueFormatter = createNumberFormatter(4);
      } else if (isDateTimeType(column.data_type)) {
        definition.cellRenderer = DateTimeRenderer;
      } else if (isDateType(column.data_type)) {
        definition.cellRenderer = DateRenderer;
      } else if (isStructuredType(column.data_type)) {
        // The renderer compacts the cell for reading; the formatter is what
        // clipboard and CSV/Excel export read, so it stays lossless — objects
        // become JSON and stored text passes through byte-for-byte.
        definition.cellRenderer = ({ value }) => compactJson(value);
        definition.valueFormatter = ({ value }) =>
          value !== null && typeof value === 'object' ? JSON.stringify(value) : value;
      }
      return definition;
    }), [keyColumn, metadata]);

  const fieldNames = useMemo(() => Object.fromEntries(
    (metadata?.columns || []).map((column) => [
      column.name,
      column.name.replaceAll('_', ' '),
    ])
  ), [metadata]);

  const transformData = useMemo(() => {
    const recencyColumn = metadata?.recency_column;
    if (!recencyColumn || recencyColumn === 'MODIFIED_DATE') return undefined;
    return (rows) => rows.map((historyRow) => {
      const { [recencyColumn]: modifiedDate, ...normalizedRow } = historyRow;
      return { ...normalizedRow, MODIFIED_DATE: modifiedDate };
    });
  }, [metadata?.recency_column]);

  return (
    <HistoryModal open={open} onClose={onClose} title={`Deal History — ${title}`}>
      <HistoryGrid
        fetchHistory={() => fetchDealTeamHistory(metadata.name, keyValue)}
        fetchParams={[open, metadata.name, keyValue]}
        transformData={transformData}
        columns={columns}
        fieldNames={fieldNames}
        emptyMessage="No saved history is available for this deal."
        defaultColDef={{ minWidth: 130, tooltipShowDelay: 500 }}
      />
    </HistoryModal>
  );
};

export default DealTeamHistoryModal;
